// Frameworks
import ReactDOM from "react-dom";
import { BrowserRouter, Route } from "react-router-dom";
import { Provider } from "react-redux";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
// Custom imports
import i18n from "lib/i18n"; // Translation configuration.
import App from "views/_app";
import ErrorBoundary from "components/common/ErrorBoundary";
import AutoLogOut from "lib/AutoLogOut";
import ValidateHash from "lib/ValidateHash";
import theme from "lib/theme";
import store from "store/index.store";
import { StrictMode } from "react";
import * as serviceWorkerRegistration from "./serviceWorkerRegistration";
import { show } from "store/reducers/notifications.reducer";
import MaintenanceGate from "components/common/MaintenanceGate";

const notify = (type: "success" | "info" | "warning", msg: string) => {
  store.dispatch(show({ type, msg }));
};

const isStandalonePwa = () => {
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    nav.standalone === true
  );
};

const isMobileLikeDevice = () => {
  const userAgent = window.navigator.userAgent.toLowerCase();
  return (
    /android|iphone|ipad|ipod/.test(userAgent) ||
    window.matchMedia("(max-width: 900px)").matches ||
    window.matchMedia("(pointer: coarse)").matches
  );
};

const promptServiceWorkerUpdate = (
  registration: ServiceWorkerRegistration
) => {
  if (!registration.waiting) {
    return;
  }

  if (!isMobileLikeDevice() && !isStandalonePwa()) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
    return;
  }

  const updateAvailable = i18n.t(
    "global:pwa.updateAvailable",
    "A new version of tradEAsy is available."
  );
  const updateConfirm = i18n.t(
    "global:pwa.updateConfirm",
    "Do you want to update now?"
  );

  let refreshing = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (refreshing) {
      return;
    }
    refreshing = true;
    window.location.reload();
  });

  if (window.confirm(`${updateAvailable}\n\n${updateConfirm}`)) {
    registration.waiting.postMessage({ type: "SKIP_WAITING" });
  } else {
    notify(
      "info",
      i18n.t(
        "global:pwa.updateDeferred",
        "The new version will be applied when you close and reopen the app."
      )
    );
  }
};

if (process.env.NODE_ENV === "production") {
  window.addEventListener("offline", () => {
    notify(
      "warning",
      i18n.t(
        "global:pwa.offline",
        "You are offline. Some features may be unavailable."
      )
    );
  });

  window.addEventListener("online", () => {
    notify(
      "success",
      i18n.t("global:pwa.online", "Connection restored.")
    );
  });
}

ReactDOM.render(
  <StrictMode>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <MaintenanceGate>
        <BrowserRouter>
          <ErrorBoundary>
            <Provider store={store}>
              <AutoLogOut idleMinutes={120} />
              <ValidateHash idleMinutes={5} />
              <Route path="/:page?" render={() => <App />} />
            </Provider>
          </ErrorBoundary>
        </BrowserRouter>
      </MaintenanceGate>
    </ThemeProvider>
  </StrictMode>,

  document.querySelector("#root")
);

serviceWorkerRegistration.register({
  onSuccess: () => {
    notify(
      "success",
      i18n.t(
        "global:pwa.readyOffline",
        "The app can now work offline for static content."
      )
    );
  },
  onUpdate: (registration) => {
    promptServiceWorkerUpdate(registration);
  },
});
